import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_LOCALE,
  isLocalizedRoute,
  LOCALE_META,
  LOCALES,
  localizePath,
  splitLocalePath,
  useLocale,
  useTranslation,
  type Locale,
} from "@thyra/landing-i18n";
import { SITE_URL } from "@/src/env";
import { serializeJsonLd, type SchemaObject } from "@/src/lib/json-ld";

const SITE_NAME = "Thyra AI";

export type { SchemaObject };

type SEOProps = {
  title?: string;
  description?: string;
  /** Unprefixed site path; defaults to the current route. */
  canonicalPath?: string;
  image?: string;
  noindex?: boolean;
  /** With noindex: keep links followable ("noindex, follow"). */
  follow?: boolean;
  schema?: SchemaObject | SchemaObject[];
  /** Social image alt text; defaults to the generic site card alt. */
  imageAlt?: string;
  /**
   * Set for blog posts: og:type becomes "article" and the card image (whose
   * size is unknown) is not announced as 1200x630.
   */
  article?: { publishedTime: string; modifiedTime: string; tags: string[] };
  /**
   * Language of the page's CONTENT when it differs from the surrounding UI
   * (an English post under /de/blog/...): drives canonical, og:locale and
   * the canonical URL's prefix.
   */
  contentLocale?: Locale;
  /**
   * Explicit hreflang set (unprefixed `path` per locale) replacing the
   * default "same path in every locale". `[]` = no alternates. x-default
   * points at the English entry when there is one.
   */
  alternates?: { locale: Locale; path: string }[];
};

/** Absolute URL of a site path in the given locale (no trailing slash). */
export function absoluteUrl(path: string, locale: Locale): string {
  const localized = localizePath(path, locale);
  return `${SITE_URL}${localized === "/" ? "" : localized}`;
}

/** Social card for a locale (see scripts/generate-og.mjs). */
export function ogImagePath(locale: Locale): string {
  return `/og/${LOCALE_META[locale].slug || DEFAULT_LOCALE}.png`;
}

export function SEO({
  title,
  description,
  canonicalPath,
  image,
  noindex = false,
  follow = false,
  schema,
  imageAlt,
  article,
  contentLocale,
  alternates: explicitAlternates,
}: SEOProps) {
  const { t } = useTranslation("seo");
  const location = useLocation();
  const currentLocale = useLocale();

  const { path } = splitLocalePath(canonicalPath ?? location.pathname);
  // English-only routes (legal pages, 404) are always described as English,
  // whatever language the surrounding navigation is in.
  const localized = isLocalizedRoute(path);
  const locale = contentLocale ?? (localized ? currentLocale : DEFAULT_LOCALE);

  const pageTitle = title ?? t("home.title");
  const pageDescription = description ?? t("home.description");
  const canonical = absoluteUrl(path, locale);
  const robots = noindex
    ? follow
      ? "noindex, follow"
      : "noindex, nofollow"
    : "index, follow";

  const imagePath = image ?? ogImagePath(locale);
  const imageUrl = imagePath.startsWith("http")
    ? imagePath
    : `${SITE_URL}${imagePath.startsWith("/") ? "" : "/"}${imagePath}`;

  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];
  const alternates: { locale: Locale; href: string }[] = noindex
    ? []
    : explicitAlternates
      ? explicitAlternates.map((a) => ({
          locale: a.locale,
          href: absoluteUrl(a.path, a.locale),
        }))
      : localized
        ? LOCALES.map((l) => ({ locale: l, href: absoluteUrl(path, l) }))
        : [];
  const xDefault = alternates.find((a) => a.locale === DEFAULT_LOCALE);

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {/* A noindex page has no canonical: it would point at a URL we are
          asking engines not to index. (The 404 page pins /404 and is exempt.) */}
      {(!noindex || canonicalPath === "/404") && (
        <link rel="canonical" href={canonical} />
      )}
      <meta name="robots" content={robots} />

      {/* hreflang: every language version lists all versions, itself
          included, plus x-default for visitors matching none of them. */}
      {alternates.map((a) => (
        <link
          key={a.locale}
          rel="alternate"
          hrefLang={a.locale}
          href={a.href}
        />
      ))}
      {xDefault && (
        <link rel="alternate" hrefLang="x-default" href={xDefault.href} />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      {!article && <meta property="og:image:width" content="1200" />}
      {!article && <meta property="og:image:height" content="630" />}
      <meta property="og:image:alt" content={imageAlt ?? t("og.alt")} />
      <meta property="og:type" content={article ? "article" : "website"} />
      {article && (
        <meta
          property="article:published_time"
          content={article.publishedTime}
        />
      )}
      {article && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article?.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={LOCALE_META[locale].og} />
      {alternates
        .filter((a) => a.locale !== locale)
        .map((a) => (
          <meta
            key={a.locale}
            property="og:locale:alternate"
            content={LOCALE_META[a.locale].og}
          />
        ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={imageAlt ?? t("og.alt")} />

      {/* JSON-LD schema */}
      {schemas.map((s, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serializeJsonLd(s) }}
        />
      ))}
    </Helmet>
  );
}
